/* ==========================================================================
   Simpatico V2 — hero promos
   Decides whose turn it is; every bit of motion is CSS.

   The active tab's fill animation is the clock: when it ends, the next promo
   comes in. Holding (hover, focus, hidden tab, scrolled away) pauses that
   animation, so a held promo resumes exactly where it stopped. Picking a tab,
   swiping, or pressing pause stops rotation for good; play restarts it.

   Reports view_promotion / select_promotion to GA4 (main-window gtag) and to
   Shopify customer events, so the offers can be judged on clicks.

   No dependencies. Safe to load deferred and to re-run on section reload.
   ========================================================================== */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  var seen = {};

  function report(name, slide, index) {
    var promo = {
      promotion_id: slide.getAttribute('data-promo-id'),
      promotion_name: slide.getAttribute('data-promo-name'),
      creative_slot: 'hero_promos_' + (index + 1)
    };
    try {
      if (typeof window.gtag === 'function') {
        window.gtag('event', name, {
          promotion_id: promo.promotion_id,
          promotion_name: promo.promotion_name,
          creative_slot: promo.creative_slot,
          items: [{ item_id: promo.promotion_id, item_name: promo.promotion_name }]
        });
      }
    } catch (e) {}
    try {
      if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
        window.Shopify.analytics.publish('simpatico_' + name, promo);
      }
    } catch (e) {}
  }

  /* -- Level with the headline --------------------------------------------- */
  /* From 1200px the docked panel takes the hero headline block's exact top and
     bottom lines (eyebrow to buttons), so the hero reads as two columns. It is
     measured because that block is whatever height the hero settings make it.
     Without JS the panel simply keeps the bottom-corner dock. */
  function levelWithHero(root) {
    if (!root.classList.contains('s2-hp--level')) return;
    var heroWrap = root.parentElement && root.parentElement.previousElementSibling;
    var hero = heroWrap && heroWrap.querySelector('.s2-hero');
    var block = heroWrap && heroWrap.querySelector('.s2-hero__content');
    if (!hero || !block) return;
    var desktop = window.matchMedia('(min-width: 1200px)');

    function measure() {
      if (!desktop.matches) {
        root.style.removeProperty('--s2-hp-dock-bottom');
        root.style.removeProperty('--s2-hp-dock-h');
        return;
      }
      var h = hero.getBoundingClientRect();
      var b = block.getBoundingClientRect();
      root.style.setProperty('--s2-hp-dock-bottom', Math.max(0, Math.round(h.bottom - b.bottom)) + 'px');
      root.style.setProperty('--s2-hp-dock-h', Math.round(b.height) + 'px');
    }

    measure();
    if ('ResizeObserver' in window) {
      var observer = new ResizeObserver(measure);
      observer.observe(hero);
      observer.observe(block);
    }
    desktop.addEventListener('change', measure);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(measure);
    window.addEventListener('load', measure);
  }

  function HeroPromos(root) {
    levelWithHero(root);

    var slides = root.querySelectorAll('.s2-hp__slide');
    var tabs = root.querySelectorAll('[role="tab"]');
    var stage = root.querySelector('.s2-hp__stage');
    var toggle = root.querySelector('.s2-hp__toggle');
    var count = slides.length;
    var index = 0;
    var stopped = reduceMotion.matches;
    var holds = { hover: false, focus: false, hidden: document.hidden, away: false, editor: false };
    var timers = [];

    function isHeld() {
      for (var key in holds) if (holds[key]) return true;
      return false;
    }

    function trackView() {
      if (holds.hidden || holds.away) return;
      var id = slides[index].getAttribute('data-promo-id');
      if (seen[id]) return;
      seen[id] = true;
      report('view_promotion', slides[index], index);
    }

    function sync() {
      root.classList.toggle('is-running', !stopped);
      root.classList.toggle('is-stopped', stopped);
      root.classList.toggle('is-held', isHeld());
      if (toggle) {
        toggle.setAttribute('aria-label', toggle.getAttribute(stopped ? 'data-label-play' : 'data-label-pause'));
      }
      // Announce changes only when a person caused them.
      if (stage) stage.setAttribute('aria-live', stopped ? 'polite' : 'off');
    }

    function later(fn, ms) { timers.push(window.setTimeout(fn, ms)); }

    function go(next, byUser) {
      next = (next + count) % count;
      if (byUser) stopped = true;
      if (next === index) { sync(); return; }

      var current = slides[index];
      var incoming = slides[next];

      current.classList.remove('is-live', 'is-enter');
      current.classList.add('is-out');
      current.setAttribute('inert', '');
      later(function () { current.classList.remove('is-out'); }, 260);

      incoming.classList.remove('is-out', 'is-enter');
      incoming.removeAttribute('inert');
      stage.classList.remove('is-wiping');
      void incoming.offsetWidth; // restart the wipe on a promo that has run before
      incoming.classList.add('is-live', 'is-enter');
      stage.classList.add('is-wiping');
      later(function () {
        incoming.classList.remove('is-enter');
        stage.classList.remove('is-wiping');
      }, 620);

      if (tabs.length) {
        tabs[index].setAttribute('aria-selected', 'false');
        tabs[index].tabIndex = -1;
        tabs[next].setAttribute('aria-selected', 'true');
        tabs[next].tabIndex = 0;
      }

      index = next;
      sync();
      trackView();
    }

    // Clicks count as selections even when a promo only shows by itself.
    Array.prototype.forEach.call(slides, function (slide, i) {
      var link = slide.querySelector('.s2-hp__link');
      if (link) link.addEventListener('click', function () { report('select_promotion', slide, i); });
    });

    if (count < 2) {
      holds.away = true;
      watchViewport();
      return;
    }

    /* -- Clock ------------------------------------------------------------- */
    root.addEventListener('animationend', function (event) {
      if (event.animationName !== 's2-hp-fill' || stopped || isHeld()) return;
      if (!event.target.closest('[aria-selected="true"]')) return;
      go(index + 1, false);
    });

    /* -- Tabs: click, and arrow keys with automatic activation -------------- */
    Array.prototype.forEach.call(tabs, function (tab, i) {
      tab.addEventListener('click', function () { go(i, true); });
      tab.addEventListener('keydown', function (event) {
        var target = null;
        if (event.key === 'ArrowRight') target = index + 1;
        else if (event.key === 'ArrowLeft') target = index - 1;
        else if (event.key === 'Home') target = 0;
        else if (event.key === 'End') target = count - 1;
        if (target === null) return;
        event.preventDefault();
        go(target, true);
        tabs[index].focus();
      });
    });

    if (toggle) {
      toggle.addEventListener('click', function () {
        stopped = !stopped;
        // Pressing play is an explicit request: it overrides the hover and focus
        // holds the press itself created, until the pointer or focus comes back.
        if (!stopped) { holds.hover = false; holds.focus = false; }
        sync();
      });
    }

    /* -- Holds ------------------------------------------------------------- */
    root.addEventListener('pointerenter', function (event) {
      if (event.pointerType === 'mouse') { holds.hover = true; sync(); }
    });
    root.addEventListener('pointerleave', function (event) {
      if (event.pointerType === 'mouse') { holds.hover = false; sync(); }
    });
    root.addEventListener('focusin', function (event) {
      if (event.target === toggle) return;
      holds.focus = true;
      sync();
    });
    root.addEventListener('focusout', function (event) {
      if (event.relatedTarget && root.contains(event.relatedTarget)) return;
      holds.focus = false;
      sync();
    });
    document.addEventListener('visibilitychange', function () {
      holds.hidden = document.hidden;
      sync();
      trackView();
    });

    /* -- Swipe (touch only; vertical scrolling is left alone) --------------- */
    var startX = null;
    var startY = null;
    var swiped = false;
    stage.addEventListener('pointerdown', function (event) {
      if (event.pointerType === 'mouse') return;
      startX = event.clientX;
      startY = event.clientY;
      swiped = false;
    });
    stage.addEventListener('pointerup', function (event) {
      if (startX === null) return;
      var dx = event.clientX - startX;
      var dy = event.clientY - startY;
      startX = null;
      if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.5) {
        swiped = true;
        go(index + (dx < 0 ? 1 : -1), true);
      }
    });
    stage.addEventListener('pointercancel', function () { startX = null; });
    // A swipe that ends on the link must not also follow it.
    stage.addEventListener('click', function (event) {
      if (!swiped) return;
      swiped = false;
      event.preventDefault();
      event.stopPropagation();
    }, true);

    reduceMotion.addEventListener('change', function () {
      if (reduceMotion.matches) { stopped = true; sync(); }
    });

    /* -- Theme editor: show the block being edited, and hold there ---------- */
    root.addEventListener('shopify:block:select', function (event) {
      var slide = event.target.closest('.s2-hp__slide');
      var i = Array.prototype.indexOf.call(slides, slide);
      holds.editor = true;
      if (i > -1) go(i, false);
      sync();
    });
    root.addEventListener('shopify:block:deselect', function () {
      holds.editor = false;
      sync();
    });

    watchViewport();

    function watchViewport() {
      if (!('IntersectionObserver' in window)) { holds.away = false; trackView(); return; }
      new IntersectionObserver(function (entries) {
        holds.away = !entries[0].isIntersecting;
        if (count > 1) sync();
        trackView();
      }, { threshold: 0.5 }).observe(root.querySelector('.s2-hp__panel') || root);
    }

    root.classList.add('is-ready');
    sync();
  }

  function init(scope) {
    Array.prototype.forEach.call((scope || document).querySelectorAll('[data-s2-hp]'), function (root) {
      if (root.hasAttribute('data-s2-ready')) return;
      root.setAttribute('data-s2-ready', 'true');
      HeroPromos(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (event) { init(event.target); });
})();
